import React, { useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import Placeholder from 'components/Placeholder';
import FlashMessage from 'components/FlashMessage/FlashMessage';

import { duckify, singleDuck } from '../../utils/duckify';
import { customerGenderConfig, customers as customersDuck } from '../../ducks';
import './styles/GenderConfig.scss';
import FontAwesome from '../../utils/FA';

function GenderConfig({
  allDucksReady, genderConfig, dispatch, customer
}) {
  if (allDucksReady && !customer.modules.includes('hide_gender_config')) {
    const [formValues, updateFormValues] = useState(genderConfig);
    const [flashSuccess, setFlashSuccess] = useState(false);
    const [flashErrors, setFlashErrors] = useState({});

    const onSubmit = () => {
      dispatch(customerGenderConfig.actions.save(formValues)).then((response) => {
        const { errors } = response.entities.customerGenderConfig[genderConfig._id];
        if (Object.keys(errors).length === 0) {
          setFlashSuccess(true);
        } else {
          setFlashErrors(errors);
        }
      }).catch((response) => {
        setFlashErrors(response.error);
      });
    };

    const updateForm = (key, event) => {
      const currentValues = { ...formValues };
      if (key === 'gender_options') {
        if (event.target.checked) {
          currentValues.gender_options.push(event.target.value);
        } else {
          currentValues.gender_options = currentValues.gender_options.filter(val => val !== event.target.value);
        }
      } else {
        currentValues[event.target.value] = !!event.target.checked;
      }

      updateFormValues(currentValues);
    };

    return (
      <form>
        {flashSuccess && <FlashMessage type='success' message='Configuration successfully updated.' />}

        <h1>Gender Field Configuration</h1>
        {flashErrors && (
          Object.keys(flashErrors).map((key) => <FlashMessage type='danger' message={flashErrors[key]} />)
        )}

        <div className='gender-config-section'>
          <h2>
            Gender Options
            <OverlayTrigger
              placement='right'
              overlay={(
                <Tooltip>
                  Selectable options for gender on forms and contact records.
                </Tooltip>
              )}
            >
              <FontAwesome icon={faInfoCircle} preNbsp />
            </OverlayTrigger>
          </h2>

          {Object.keys(genderConfig.gender_map).map((gender) => (
            <span>
              <input
                type='checkbox'
                id={gender}
                checked={formValues.gender_options.includes(gender)}
                onChange={(e) => updateForm('gender_options', e)}
                value={gender}
              />
              <label
                className='base-setup-field-checkbox-label gender-config-label'
              >
                {genderConfig.gender_map[gender]}
              </label>
            </span>
          ))}
        </div>

        <div className='gender-config-section'>
          <h2>Pronoun Options</h2>
          <div className='gender-config-section'>
            <input
              type='checkbox'
              onChange={(e) => updateForm('allow_pronouns', e)}
              id='allow_pronouns'
              value='allow_pronouns'
              checked={formValues.allow_pronouns}
            />
            <label className='base-setup-field-checkbox-label gender-config-label'>
              {' '}
              Show Pronoun
              Selection
              <OverlayTrigger
                placement='right'
                overlay={(
                  <Tooltip>
                    Shows the pronoun selection field as part of the gender field on a form or
                    contact record.
                  </Tooltip>
                )}
              >
                <FontAwesome icon={faInfoCircle} preNbsp />
              </OverlayTrigger>
            </label>
          </div>

          <input
            type='checkbox'
            onChange={(e) => updateForm('require_pronouns', e)}
            id='require_pronouns'
            value='require_pronouns'
            checked={formValues.require_pronouns}
          />
          <label className='base-setup-field-checkbox-label gender-config-label'>
            Require Pronoun
            Selection
            <OverlayTrigger
              placement='right'
              overlay={(
                <Tooltip>
                  Requires a pronoun selection on forms where the gender field is required.
                </Tooltip>
              )}
            >
              <FontAwesome icon={faInfoCircle} preNbsp />
            </OverlayTrigger>
          </label>
        </div>

        <div className='gender-config-section'>
          <h2>Display Pronouns</h2>
          <input
            type='checkbox'
            onChange={(e) => updateForm('display_pronouns', e)}
            id='display_pronouns'
            value='display_pronouns'
            checked={formValues.display_pronouns}
          />
          <label className='base-setup-field-checkbox-label gender-config-label'>
            Display Pronouns
            <OverlayTrigger
              placement='right'
              overlay={(
                <Tooltip>
                  Displays the pronoun in header of a contact record.
                </Tooltip>
              )}
            >
              <FontAwesome icon={faInfoCircle} preNbsp />
            </OverlayTrigger>
          </label>
        </div>

        <div className='gender-config-section'>
          <h2>Relationship Options</h2>
          <input
            type='checkbox'
            onChange={(e) => updateForm('display_parent_option', e)}
            id='display_parent_option'
            value='display_parent_option'
            checked={formValues.display_parent_option}
          />
          <label className='base-setup-field-checkbox-label gender-config-label'>
            Display “Parent” option
            <OverlayTrigger
              placement='right'
              overlay={(
                <Tooltip>
                  Shows “Parent” as a relationship option on Inquiry and Application forms, in addition to “Mother”,
                  “Father”, “Guardian”, etc.
                </Tooltip>
              )}
            >
              <FontAwesome icon={faInfoCircle} preNbsp />
            </OverlayTrigger>
          </label>
        </div>

        <div className='gender-config-section'>
          <Button className='btn' onClick={onSubmit}>Submit</Button>
        </div>
      </form>
    );
  } else if (customer.modules.includes('hide_gender_config')) {
    return (<FlashMessage type='danger' message='Gender Field Configuration has been hidden for this customer' />);
  }
  return <Placeholder.loading />;
}

GenderConfig.defaultProps = {
  genderConfig: {},
  customer: {}
};

const mapDucksToProps = () => ({
  genderConfig: customerGenderConfig,
  customer: singleDuck(customersDuck, 'current')
});

export default duckify(mapDucksToProps, () => ({}), () => ({}))(GenderConfig);
